import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo } from 'react';

import Heading from 'shopper/components/Heading';

import ErrorBoundary from 'components/ErrorBoundary';
import StopWatch from 'components/StopWatch';

import useCountdown from 'hooks/useCountdown';
import useFlashOffer from 'hooks/useFlashOffer';

import { sendEvent } from 'lib/gtag';
import { sliceWithEllipsis } from 'lib/string';

import CommentsArea from '../CommentsArea';
import ActionButton from './ActionButton';
import AlertButton from './Actions/AlertButton';
import Context from './Actions/Context';
import LikeButton from './Actions/LikeButton';
import Share from './Actions/Share';
import CouponField from './CouponField';
import Hero from './Hero';
import Price from './Price';
import PublishDetails from './PublishDetails';

import OFFER from 'constants/offer';

const isOfferShareable = (offerStatusName) =>
  offerStatusName !== OFFER.STATUSES.WAITING;

const Tags = dynamic(() => import('./Tags'));

const Main = ({
  aliasId,
  authorCount,
  authors,
  gaEventCategory,
  isFinished,
  isOfferCommentsAllowed,
  keywords,
  mostRelevantKeyword,
  offerCommentsCount,
  offerCoupon,
  offerCta = null,
  offerId,
  offerInstructions,
  offersToCompare,
  offerLikes,
  offerOldPrice,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerPublished,
  offerSlug,
  offerStatusName,
  offerTags = [],
  offerTitle,
  offerUserVisibility,
  reviewRate = 0,
  reviews = [],
  serverComments,
  storeDomain,
  storeImage,
  storeName,
  totalReviews,
  userId,
  userName,
  userSpecialtyName,
  userUsername,
}) => {
  const hasComparator = (offersToCompare ?? []).length > 0;
  const flashOffer = useFlashOffer(offerId);
  const countdown = useCountdown(flashOffer?.targetDate || new Date(0));
  const showStopWatch = Boolean(flashOffer);

  const onOfferTitleClick = () => {
    sendEvent({ action: 'title', category: gaEventCategory });
  };

  return (
    /*
      Card único envolvendo título + imagem/infos + comentários — segue
      o layout do Pelando. A separação visual é feita só com a borda;
      o bg acompanha o do body (`bg-neutral-high-100` /
      `bg-neutral-low-500`, aplicado via `data-global-flatbg` na
      FlatLayout). O H1 é o primeiro elemento e ocupa a largura toda
      (acima do split imagem/comentários).
    */
    <section className="rounded-5 border border-neutral-high-300 bg-neutral-high-100 p-4 dark:border-neutral-low-300 dark:bg-neutral-low-500 lg:p-6">
      <Heading
        as="h1"
        className="text-pretty font-bold leading-[1.33] text-neutral-low-500 dark:text-neutral-high-100 lg:text-2xl lg:leading-8"
        size="size4"
        onClick={onOfferTitleClick}
      >
        {sliceWithEllipsis(offerTitle, 180)}
      </Heading>
      <div className="mt-4 flex flex-col gap-4 lg:h-[585px] lg:flex-row lg:gap-8">
        <div className="flex min-w-0 flex-col gap-y-4 lg:w-[560px] xl:w-[650px]">
          <Hero
            gaEventCategory={gaEventCategory}
            isFinished={isFinished}
            offerId={offerId}
            offerPhoto={offerPhoto}
            offerTitle={offerTitle}
            overlayActions={
              <>
                <Context
                  gaEventCategory={gaEventCategory}
                  offerCoupon={offerCoupon}
                  offerId={offerId}
                  offerInstructions={offerInstructions}
                  offerPrice={offerPrice}
                  offerPriceType={offerPriceType}
                  offerStatusName={offerStatusName}
                  userId={userId}
                />
                {isOfferShareable(offerStatusName) && (
                  <Share
                    gaEventCategory={gaEventCategory}
                    offerPrice={offerPrice}
                    offerPriceType={offerPriceType}
                    offerTitle={offerTitle}
                  />
                )}
              </>
            }
          />
          {/*
            Linha imediata abaixo da imagem: curtidas | cupom | ir à
            loja. mb-[15px] separa visualmente do preço abaixo.
            Quando não há cupom, o ActionButton expande pra preencher
            todo o espaço disponível (sem slot vazio de 50%).
          */}
          <div className="mb-[15px] flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="shrink-0">
              <LikeButton
                gaEventCategory={gaEventCategory}
                likesCount={offerLikes}
                offerId={offerId}
                size="size3"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2 lg:flex-row">
              {isFinished ? (
                <div className="min-w-0 flex-1">
                  <ActionButton
                    gaEventCategory={gaEventCategory}
                    hasComparator={hasComparator}
                    isFinished={isFinished}
                    mostRelevantKeyword={mostRelevantKeyword}
                    offerCoupon={offerCoupon}
                    offerCta={offerCta}
                    offerId={offerId}
                    offerSlug={offerSlug}
                    offerUserVisibility={offerUserVisibility}
                  />
                </div>
              ) : (
                <>
                  {offerCoupon && (
                    <div className="w-full lg:w-1/2">
                      <CouponField
                        className="w-full"
                        coupon={offerCoupon}
                        gaEventCategory={gaEventCategory}
                      />
                    </div>
                  )}
                  <div
                    className={
                      offerCoupon ? 'w-full lg:w-1/2' : 'min-w-0 flex-1'
                    }
                  >
                    <ActionButton
                      gaEventCategory={gaEventCategory}
                      hasComparator={hasComparator}
                      isFinished={isFinished}
                      mostRelevantKeyword={mostRelevantKeyword}
                      offerCoupon={offerCoupon}
                      offerCta={offerCta}
                      offerId={offerId}
                      offerSlug={offerSlug}
                      offerUserVisibility={offerUserVisibility}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          {showStopWatch && (
            <ErrorBoundary>
              <StopWatch
                hours={countdown.hours}
                minutes={countdown.minutes}
                offerId={offerId}
                phase={flashOffer.phase}
                seconds={countdown.seconds}
                size="lg"
              />
            </ErrorBoundary>
          )}
          <Price
            offerOldPrice={offerOldPrice}
            offerPrice={offerPrice}
            offerPriceType={offerPriceType}
            size="large"
          />
          {offerTags.length > 0 && <Tags tags={offerTags} />}
          {/*
            Última linha: "Vendido por" + "Criar alerta", empurrada pro
            rodapé com `mt-auto`.
          */}
          <div className="mt-auto flex items-center justify-between gap-3">
            <PublishDetails
              storeDomain={storeDomain}
              storeImage={storeImage}
              storeName={storeName}
            />
            <AlertButton
              aliasId={aliasId}
              gaEventCategory={gaEventCategory}
              keywordLabel={mostRelevantKeyword}
              keywords={keywords}
              offerStatusName={offerStatusName}
            />
          </div>
        </div>
        <CommentsArea
          aliasId={aliasId}
          authorCount={authorCount}
          authors={authors}
          className="hidden lg:flex lg:flex-1"
          gaEventCategory={gaEventCategory}
          isOfferCommentsAllowed={isOfferCommentsAllowed}
          offerCommentsCount={offerCommentsCount}
          offerId={offerId}
          offerInstructions={offerInstructions}
          offerPublished={offerPublished}
          offerStatusName={offerStatusName}
          reviewRate={reviewRate}
          reviews={reviews}
          reviewsListClassName="min-h-0 flex-1 overflow-y-auto pr-1"
          serverComments={serverComments}
          totalReviews={totalReviews}
          userId={userId}
          userName={userName}
          userSpecialtyName={userSpecialtyName}
          userUsername={userUsername}
        />
      </div>
    </section>
  );
};

Main.propTypes = {
  aliasId: PropTypes.number,
  authorCount: PropTypes.number.isRequired,
  authors: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  isFinished: PropTypes.bool.isRequired,
  isOfferCommentsAllowed: PropTypes.bool.isRequired,
  keywords: PropTypes.arrayOf(PropTypes.string),
  mostRelevantKeyword: PropTypes.string.isRequired,
  offerCommentsCount: PropTypes.number.isRequired,
  offerCoupon: PropTypes.string,
  offerCta: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string,
  offerLikes: PropTypes.number.isRequired,
  offersToCompare: PropTypes.arrayOf(PropTypes.shape()),
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerPublished: PropTypes.string,
  offerSlug: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(PropTypes.shape()),
  offerTitle: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string.isRequired,
  reviewRate: PropTypes.number,
  // Shape achatada — igual ao retorno do endpoint `GET /v4/offers/{slug}/reviews`
  // (ver `ReviewListResponse::format` no backend). Antes este PropType era
  // `{ data: {...} }` por engano, ficando fora de sync com o shape real
  // consumido pelo `ReviewsList`.
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      relevance: PropTypes.number,
      text: PropTypes.string.isRequired,
      raw: PropTypes.string,
      date: PropTypes.string.isRequired,
      likes: PropTypes.number,
      statusName: PropTypes.string,
      stars: PropTypes.number,
      user: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        username: PropTypes.string,
        photo: PropTypes.string,
        typeName: PropTypes.string,
        level: PropTypes.number,
        specialityName: PropTypes.string,
        link: PropTypes.string,
      }),
    })
  ),
  serverComments: PropTypes.arrayOf(
    PropTypes.shape({
      commentId: PropTypes.number.isRequired,
      parentId: PropTypes.number,
      userId: PropTypes.number.isRequired,
      commentText: PropTypes.string.isRequired,
      commentRaw: PropTypes.string.isRequired,
      commentDate: PropTypes.string.isRequired,
      commentLikes: PropTypes.number.isRequired,
      commentStatusName: PropTypes.string.isRequired,
      commentFixed: PropTypes.bool.isRequired,
      relevance: PropTypes.number.isRequired,
      commentStars: PropTypes.number,
      reviewAliasId: PropTypes.number,
      edited: PropTypes.bool.isRequired,
      isReview: PropTypes.bool.isRequired,
      userLevel: PropTypes.number.isRequired,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userSpecialtyName: PropTypes.string,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string,
      replies: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    })
  ).isRequired,
  storeDomain: PropTypes.string.isRequired,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  totalReviews: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userUsername: PropTypes.string.isRequired,
};

export default memo(Main);
